// Text — the typographic primitive, pure.
//
// One place that owns the type scale so no other component hardcodes a
// font-size, weight, or text colour: they compose Text (or reach for the
// same --text-*/--weight-*/--color-* tokens through it). Rebuilt against
// the foundations (.claude/research/design/05) + visual language (.claude/research/design/06).
//
// Design choices encoded here:
//   • DISTINCT typed props, no anonymous shapes — `size`, `weight`, `tone`
//     and `as` are each a small named union (discriminated by their literal
//     value); the public surface is `TextProps`, nothing else leaks.
//   • `tone` is a TYPOGRAPHIC lever (how the text reads on the ground), not
//     a state signal. `accent` is the one place ink spends the accent — use
//     it only for the agent's voice / a single primary emphasis, never as
//     decoration. `danger`/`success`/`warning` colour the glyphs; the
//     COMPOSING component still owns SC 1.4.1 (pair the colour with an icon
//     or a word — colour is never the sole channel).
//   • `as` chooses the element without changing meaning, so it stays a plain
//     union rather than a discriminated variant; `createElement` renders it
//     with no `as`-cast (the union is already keyof-valid intrinsic tags).
//
// Pure/props-driven: no side effects, no state. `size`/`weight`/`tone` pick
// the look, `as` picks the tag, `children` is the content, `className`
// composes. Nothing reaches out.

import { createElement, type ReactNode } from "react";
import "./Text.css";

type TextSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
type TextWeight = "regular" | "medium" | "semibold";
type TextTone =
    | "default"
    | "dim"
    | "faint"
    | "accent"
    | "danger"
    | "success"
    | "warning";
// The elements Text is allowed to become — inline, paragraph, generic block,
// or form label. All are valid intrinsic tags, so the union renders with no cast.
export type TextElement = "span" | "p" | "div" | "label";

export type TextProps = {
    readonly size?: TextSize;
    readonly weight?: TextWeight;
    readonly tone?: TextTone;
    readonly as?: TextElement;
    readonly children?: ReactNode;
    readonly className?: string;
    // Only meaningful when `as="label"` — wires the label to its control's
    // id so the control gets an accessible name (WCAG 4.1.2 / select-name).
    readonly htmlFor?: string;
};

export const Text = ({
    size = "md",
    weight = "regular",
    tone = "default",
    as = "span",
    children,
    className,
    htmlFor,
}: TextProps) =>
    createElement(
        as,
        {
            className: `bilda-text bilda-text--${size} bilda-text--w-${weight} bilda-text--${tone}${
                className ? ` ${className}` : ""
            }`,
            htmlFor,
        },
        children,
    );
